import type { ReactNode } from "react";

interface PageHeaderProps {
  /** Page title (the `<h1>`). */
  title: string;
  /** Optional one-line description shown under the title. */
  description?: string;
  /** Right-aligned actions (buttons, filters, refresh, etc.). */
  actions?: ReactNode;
  /** A short label beside the title, e.g. "Beta" for a page still settling. */
  tag?: string;
}

/**
 * Uniform page header: title + optional description + right-aligned actions.
 * One component so every page reads the same. The active scope is already shown
 * in the global scope bar, so it is intentionally NOT repeated here.
 */
export function PageHeader({
  title,
  description,
  actions,
  tag,
}: PageHeaderProps) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 space-y-1.5">
        {/* Face, size, weight and tracking all come from the global `h1`
            rhythm — an inline `text-2xl` here used to clobber its
            `sm:text-[1.65rem]` bump. */}
        <div className="flex flex-wrap items-center gap-2">
          <h1>{title}</h1>
          {tag && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
              {tag}
            </span>
          )}
        </div>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {actions && (
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {actions}
        </div>
      )}
    </div>
  );
}
